<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import ApStories from '@/components/apostatudo/organisms/Stories.vue';
import { useModalStore } from '@/stores/apostatudo/modal';
import { useStoriesStore } from '@/stores/apostatudo/stories';
import { useWalletStore } from '@/stores/wallet';
import { copyToClipboard } from '@/utils/apostatudo/clipboard';
import { useVipStatus } from '@/composables/useVipStatus';
import {
  compactXp,
  hexAlpha,
  mixColor,
  roundProgressPercent,
} from '@/utils/levels';

// Header do painel Minha Conta V2. Mobile ("Perfil Box" do Figma): saudação,
// saldo em pill, avatar com "Alterar", ID e barra de nível (real, sem gate
// de VIP). Desktop ("Perfil Desktop", node 19771:52160): linha horizontal
// com avatar+nome+VIP, card de nível e botões Depositar/Sacar.
const { userCurrency, userRegisteredAt } = useUserData();

// Mesma fonte do menu antigo (UserMenuItems.vue): prioriza o apelido da
// Smartico, só cai pro primeiro nome do cadastro se não existir. Antes o V2
// lia direto de useUserData() e nunca refletia o apelido editado pelo lápis
// (useSmarticoUserInfos().openChangeNickname(), ver handler abaixo).
const { displayName: firstName } = useSmarticoNickname();

const { isNewAuthButtonsEnabled, isNewDepositButtonPillEnabled } =
  useFeatureFlagsNew();

// Mesmo gate do selo PIX/SPEI do botão Depositar do header antigo
// (components/user/header/Buttons.vue#showButtonDepositPill) — reaproveita a
// lógica (moeda + feature flags), não o componente visual, que aqui é
// compacto e nos tokens v2-*.
const showDepositPill = computed(() => {
  if (userCurrency.value === 'BRL' || userCurrency.value === 'MXN') {
    return isNewAuthButtonsEnabled.value && isNewDepositButtonPillEnabled.value;
  }

  return false;
});

const walletStore = useWalletStore();
const { currentUserID } = storeToRefs(walletStore);
const { walletRealFormatted } = useWallet();

// Mesmo dropdown de moedas do header antigo (components/user/header/
// Buttons.vue: btn-balance + UserHeaderCurrencyDropdown) — clicar no pill
// recarrega o saldo (botão da esquerda, propagação cortada) ou abre a badge
// com Coins/Diamonds/Gems (clicar no resto do pill).
const isWalletDropdownOpen = ref(false);
const walletDropdownRef = ref<HTMLElement | null>(null);

onClickOutside(walletDropdownRef, () => {
  isWalletDropdownOpen.value = false;
});

function toggleWalletDropdown() {
  isWalletDropdownOpen.value = !isWalletDropdownOpen.value;
}

const { close } = useUserV2Modal();

const smarticoUserInfosStore = useSmarticoUserInfos();
const { currentUserProfileAvatar } = storeToRefs(smarticoUserInfosStore);

// Barra do topo mobile (Figma node 22866:77529, "nav"): logo + notificações +
// Depositar + fechar. Não é uma peça nova — são os MESMOS componentes/
// composables do header real do site (components/header/Header.vue e
// components/user/header/Buttons.vue), só redesenhados no estilo V2, pra não
// depender do header do site aparecer atrás do painel.
const { hasStories, hasUnseenStories } = storeToRefs(useStoriesStore());
const modalStore = useModalStore();

const { isNotificationActived } = storeToRefs(useSmarticoConfigs());
const { unReadMessages } = storeToRefs(useSmarticoMessages());

const hasUnreadMessages = computed(() => unReadMessages.value > 0);
const unReadMessagesCount = computed(() =>
  unReadMessages.value >= 9 ? '9+' : unReadMessages.value,
);

function openInbox() {
  if (typeof window !== 'undefined' && window._smartico) {
    window._smartico.dp('dp:inbox');
  }
}

const { openDepositModal, openWithdrawModal } = usePayments();

// Mesmo gating do UserV2WalletActionButtons (desktop): Depositar some em
// exclusão/timeout/exceção; Sacar desabilita sem saque liberado/saldo real.
const { userCanOnlyWithdraw, canWithdraw } = useUserV2PaymentsGate();

// Clicar no avatar abre a troca de avatar da Smartico (mesmo fluxo do menu antigo).
function handleAvatarClick() {
  smarticoUserInfosStore.openChangeAvatar();
}

// "Membro desde abril 2025" a partir do created_at real; sem data, fallback.
const memberSince = computed(() => {
  if (!userRegisteredAt.value) return 'Membro Apostatudo';

  const date = new Date(userRegisteredAt.value);

  if (Number.isNaN(date.getTime())) return 'Membro Apostatudo';

  const label = date
    .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    .replace(' de ', ' ');

  return `Membro desde ${label}`;
});

const { openToaster } = useLayoutStore();

const { isVip, vipTier, vipManagerWhatsapp } = useVipStatus();

// Mini tag "VIP" da saudação mobile ("Olá {nome}!"): aparece pra qualquer
// tier — diferente do selo "USUÁRIO VIP" do desktop (tier 0-3 só, ver
// showVipBadge), no mobile ela convive com o chip do gerente (tier 4+).
const showVipTag = computed(() => isVip.value);

const MAX_BADGE_TIER = 3;
const showVipBadge = computed(
  () =>
    isVip.value && vipTier.value !== null && vipTier.value <= MAX_BADGE_TIER,
);

const MANAGER_TIER = 4;
const showVipManagerCard = computed(
  () => isVip.value && vipTier.value !== null && vipTier.value >= MANAGER_TIER,
);

// wa.me só aceita dígitos (sem "+", espaço ou traço) — o core manda em E.164
// ("+554488501508"), então tira tudo que não for número antes de montar o link.
async function handleTalkToManager() {
  const digits = vipManagerWhatsapp.value?.replace(/\D/g, '');

  if (!digits) {
    return;
  }

  await navigateTo(`https://wa.me/${digits}`, {
    external: true,
    open: { target: '_blank' },
  });
}

// Card de nível: mesmo dado real do dropdown antigo (components/user/header/
// UserMenuItems.vue) — nunca trava por tier VIP, sempre mostra o nível/elo
// de verdade (Smartico XP), eixo totalmente separado do tier VIP (que só
// controla o selo/chip do gerente, em outro lugar deste arquivo).
const {
  tier: levelTier,
  level,
  currentLevelLabel,
  levelProgress,
  levelXp,
  levelXpSpan,
} = useLevelsProgress();

// Cor das partículas do fundo do header: a cor do elo puxada pro azul do
// header (mesmo tom da roletinha), pra ler como "azul com um toque do elo" —
// a cor crua do elo (Prata #8FD0FF, Ouro #FFC000...) ficava lavada/estranha
// sobre o fundo primary. 0.45 = quase meio a meio.
const particlesColor = computed(() =>
  mixColor(levelTier.value.color, '#2F6BFF', 0.45),
);

const levelXpLabel = computed(
  () => `${compactXp(levelXp.value)}/${compactXp(levelXpSpan.value)} XP`,
);

const levelProgressRounded = computed(() =>
  roundProgressPercent(levelProgress.value),
);

function handleVerNiveis() {
  close();
  navigateTo('/vip/levels');
}

// Popover "Notificações" (Figma node 22854:69744, "Tooltip"): abre ao clicar
// no sino, some ao clicar fora ou apertar Esc.
const notificationsWrapperRef = ref<HTMLElement | null>(null);
const isNotificationsPopoverOpen = ref(false);

onClickOutside(notificationsWrapperRef, () => {
  isNotificationsPopoverOpen.value = false;
});

onKeyStroke('Escape', () => {
  isNotificationsPopoverOpen.value = false;
});

// Toggle real: mesma preferência de marketing do card "Quero receber
// atualizações" (Dados da conta) e do card de ofertas do dashboard — PATCH
// bff/users/self-mkt, campo mkt_accepted_at (composable compartilhado com
// ScreenNotifications.vue e ScreenProfile.vue). Desligar passa pelo modal de
// confirmação (ENG-491), montado no Shell.vue.
const {
  isActive: isNotificationsEnabled,
  isSaving: isSavingNotifications,
  requestToggle: toggleNotifications,
} = useMarketingOptIn();

// troca o texto do botão por "ID copiado!" por 3s (mesma duração/padrão do
// "Link copiado!" em ScreenRefers.vue), junto com o toast
const isIdCopied = ref(false);

async function copyUserId() {
  if (!currentUserID.value) return;

  const ok = await copyToClipboard(String(currentUserID.value));

  openToaster({
    message: ok ? 'ID copiado!' : 'Não foi possível copiar o ID.',
    type: ok ? 'success' : 'error',
  });

  const TWO_SECONDS_IN_MS = 2000;

  if (ok) {
    isIdCopied.value = true;
    setTimeout(() => {
      isIdCopied.value = false;
    }, TWO_SECONDS_IN_MS);
  }
}
</script>

<template>
  <!-- Corte em lg: (1024px), não no md: (768px) do resto do painel: a linha
       desktop (avatar+nome+card de nível 294px fixo+botões) precisa de
       ~740-810px pra caber sem sobrepor o X/sino, que são absolutos com
       espaço reservado só por um pr-[80px] chutado (não uma medida real) —
       em 768-1023px eles ficavam por cima do Depositar/Sacar. -->
  <header
    class="relative isolate z-10 flex w-full flex-col gap-[10px] rounded-b-[10px] bg-v2-primary-200 px-[10px] pb-[16px] pt-[10px] lg:gap-0 lg:rounded-none lg:py-[16px] lg:pl-[16px] lg:pr-[80px]"
  >
    <!-- Partículas na cor do elo (mesmo BaseParticlesField da roletinha), atrás
         de todo o conteúdo do header. `isolate` no <header> + -z-10 aqui:
         a camada fica entre o fundo do header e o conteúdo em fluxo. O clip é
         neste wrapper (não no <header>) pra não cortar os dropdowns absolutos
         que nascem no header (carteira, menu). intensity baixa: o header é
         denso (avatar, saldo, botões, XP), a calibragem 1 da roletinha pesa.
         travel fixo maior que a altura do bloco nos dois breakpoints. -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 -z-10 overflow-clip rounded-b-[10px] lg:rounded-none"
    >
      <BaseParticlesField
        :color="particlesColor"
        :intensity="0.25"
        :lights="false"
        travel="400px"
      />
    </div>

    <!-- ===================== MOBILE ===================== -->
    <div class="flex w-full flex-col gap-[10px] lg:hidden">
      <!-- barra do topo: logo, notificações, Depositar, fechar (ver
           comentário no script — mesmos componentes do header real) -->
      <div class="flex w-full items-center justify-between">
        <!-- sem deslocamento vertical: no Figma (23469:109859, "nav") a logo
             fica centrada na mesma linha dos ícones. O top-[10px] antigo
             imitava a logo "alta" do header real do site, que também foi
             realinhada (components/header/Header.vue). -->
        <ApHeaderLogo
          :has-stories="hasStories"
          :has-unseen-stories="hasUnseenStories"
          @click-logo="close"
          @click-stories="() => modalStore.open(ApStories)"
        />

        <div class="flex h-[30px] items-center gap-[8px]">
          <button
            class="relative flex size-[30px] items-center justify-center rounded-[9px] border border-solid"
            :class="
              hasUnreadMessages
                ? 'border-v2-secondary bg-v2-secondary/20'
                : 'border-white/40'
            "
            type="button"
            @click="openInbox"
          >
            <span
              v-if="hasUnreadMessages && isNotificationActived"
              class="absolute -top-[9px] left-[19px] flex size-[14px] items-center justify-center rounded-lg bg-v2-secondary text-[8px] font-bold leading-[9px] text-white"
            >
              {{ unReadMessagesCount }}
            </span>
            <!-- botão de abrir a caixa de entrada, não o toggle de marketing
                 (esse é só no desktop, ver bell/bell-slash mais abaixo) — ícone
                 fixo, não troca com isNotificationsEnabled. -->
            <NuxtIcon
              class="!size-[16px] text-[16px] text-white"
              name="user/notification-letter"
            />
          </button>

          <button
            class="relative flex h-[30px] items-center justify-center rounded-[9px] bg-[radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] px-[10px] text-[13px] font-medium leading-[10px] text-v2-secondary-foreground"
            type="button"
            @click="openDepositModal"
          >
            <!-- selo PIX/SPEI: gradiente branco->cinza raso, próprio deste
                 selo (não tem token/nome no Figma, diferente do gradiente
                 documentado do avatar, ver AvatarButton.vue) -->
            <span
              v-if="showDepositPill"
              class="absolute -top-[8px] right-[9px] flex h-[14px] w-[34px] items-center justify-center gap-[2px] rounded-[9px] bg-[linear-gradient(114deg,#FFF_34.75%,#E5E5E5_100%)] text-center text-[8px] font-bold leading-[11px] text-v2-background"
            >
              <template v-if="userCurrency === 'MXN'">SPEI</template>
              <template v-else>
                <NuxtIcon
                  class="!size-[10px] text-[10px]"
                  :filled="false"
                  name="payments/pix_icon"
                />
                PIX
              </template>
            </span>
            Depositar
          </button>

          <button
            aria-label="Fechar"
            class="flex size-[30px] items-center justify-center rounded-[9px] bg-white/5"
            type="button"
            @click="close"
          >
            <svg
              fill="none"
              height="30"
              viewBox="0 0 30 30"
              width="30"
            >
              <path
                d="M21.8455 10.4533L23 9.30388L20.6961 7L19.5467 8.1494L15 12.6961L10.4533 8.1494L9.30388 7L7 9.30388L8.1494 10.4533L12.6961 15L8.15448 19.5467L7 20.6961L9.30388 23L10.4533 21.8506L15 17.3039L19.5467 21.8506L20.6961 23L23 20.6961L21.8506 19.5467L17.3039 15L21.8506 10.4533H21.8455Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- avatar + coluna (nome/ID, saldo+Saque+Gerente, Depositar) — sem
           saudação "Olá" e sem cards com fundo próprio, igual ao redesign
           aprovado (Figma node 23441:104892/104909): tudo direto no fundo
           do header. Altura da coluna (19+12+35+12+35=113) bate com a
           altura do avatar (113px) de propósito. mt-[6px]: sem o fundo de
           card, o gap-[10px] do wrapper sozinho ficava colado na barra do
           logo/ícones acima — folga só nessa borda. -->
      <div class="mt-[6px] flex w-full items-start gap-[10px]">
        <UserV2AvatarButton
          :avatar-url="currentUserProfileAvatar"
          variant="mobile"
          @click="handleAvatarClick"
        />

        <div class="flex min-w-0 flex-1 flex-col gap-[12px]">
          <!-- nome+lápis+VIP à esquerda, ID+copiar à direita -->
          <div class="flex w-full items-center justify-between gap-[8px]">
            <div class="flex min-w-0 items-center gap-[5px]">
              <!-- truncate + min-w-0 na cadeia toda: apelido longo
                   empurrava o resto da linha em vez de virar "..." -->
              <p
                class="truncate text-[14px] font-bold leading-none text-white"
                :title="firstName"
              >
                {{ firstName }}
              </p>
              <button
                aria-label="Editar apelido"
                class="flex-none"
                type="button"
                @click="smarticoUserInfosStore.openChangeNickname()"
              >
                <NuxtIcon
                  class="!size-[11px] text-[11px]"
                  name="user/edit"
                />
              </button>
              <!-- mesma arte por tier do header antigo (components/vip/
                   Tag.vue, tag-{tier}.webp) no lugar do selo "VIP" — regra
                   inalterada (showVipTag, qualquer tier). -->
              <VipTag
                v-if="showVipTag"
                class="shrink-0"
                :height="17"
              />
            </div>

            <UserV2IdBadge
              v-if="currentUserID"
              class="max-w-full shrink-0"
              :copied="isIdCopied"
              :user-id="currentUserID"
              variant="mobile"
              @copy="copyUserId"
            />
          </div>

          <!-- saldo (pill branco, texto escuro — volta ao estilo antigo,
               sem ícone de refresh) + Saque + Gerente (ícone só, tier 4+) -->
          <div class="flex w-full items-center justify-between gap-[8px]">
            <div
              ref="walletDropdownRef"
              class="relative flex w-[145px] shrink-0 items-center"
            >
              <button
                class="border-solid flex h-[35px] w-full items-center justify-between gap-[8px] rounded-[12px] border border-white/[0.11] bg-white px-[11px] py-[8px]"
                type="button"
                @click="toggleWalletDropdown"
              >
                <p
                  class="truncate text-[14px] font-extrabold leading-[20px] text-v2-background"
                >
                  {{ walletRealFormatted }}
                </p>
                <span
                  class="flex size-[20px] shrink-0 items-center justify-center rounded-full bg-v2-background/10"
                >
                  <NuxtIcon
                    class="!size-[13px] text-[13px] text-v2-background"
                    name="arrows/angle_down"
                  />
                </span>
              </button>

              <UserHeaderCurrencyDropdown
                v-if="isWalletDropdownOpen"
                class="absolute left-0 top-full z-50 mt-[8px]"
                @close="isWalletDropdownOpen = false"
              />
            </div>

            <button
              class="border-solid flex h-[33px] flex-1 items-center justify-center rounded-[12px] border border-white bg-white/[0.08] text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:border-white/30 disabled:text-white/30"
              :disabled="!canWithdraw"
              type="button"
              @click="openWithdrawModal"
            >
              Saque
            </button>

            <!-- tier 4+: mesmo gate do desktop, só que ícone puro (sem
                 texto) — Figma node 23469:109859 ("button.mgr"): quadrado
                 arredondado (não círculo) e o balão de 21px exportado do
                 próprio Figma (assets/icons/social/whatsapp-filled.svg) — o
                 traço do social/whatsapp (FontAwesome) não é o mesmo desenho.
                 `filled` mantém o #25D366 do SVG (cor da marca do WhatsApp,
                 não é token v2) em vez de trocar por currentColor -->
            <button
              v-if="showVipManagerCard"
              class="border-solid flex size-[33px] shrink-0 items-center justify-center rounded-[10px] border border-v2-success bg-gradient-to-t from-v2-success/[0.17] to-v2-success/[0.55]"
              type="button"
              @click="handleTalkToManager"
            >
              <NuxtIcon
                class="!size-[21px] shrink-0 text-[21px]"
                filled
                name="social/whatsapp-filled"
              />
            </button>
          </div>

          <!-- Depositar sozinho, full-width (Saque/Gerente saíram daqui,
               ver bloco acima). v-if: em exclusão/timeout/exceção some,
               mesma regra do UserV2WalletActionButtons (desktop). -->
          <button
            v-if="!userCanOnlyWithdraw"
            class="flex h-[35px] w-full items-center justify-center rounded-[12px] bg-[radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%)] text-[14px] font-bold text-white shadow-[0_6px_8px_rgba(241,58,13,0.4)]"
            type="button"
            @click="openDepositModal"
          >
            Depositar
          </button>
        </div>
      </div>

      <!-- card de nível: sempre real, sem gate de VIP (mesma regra do
           dropdown antigo, components/user/header/UserMenuItems.vue —
           nível/elo é eixo separado do tier VIP). Sem fundo/borda próprios
           (o redesign aprovado tira o card, fica direto no fundo do
           header) — o emblema é a arte real do elo, maior que a barra e
           deixado transbordar por cima/baixo de propósito. Sem pt/pb
           próprios: o Figma tinha, mas aqui somava com o pb-[16px] do
           <header> por fora e dobrava o espaço embaixo do XP/%. -->
      <div class="flex w-full items-center gap-[10px]">
        <div class="relative h-[51px] w-[42px] shrink-0">
          <!-- brilho na cor do elo atrás do emblema — mesmo drop-shadow da
               lista "Todos os níveis" (components/levels/AllTiers.vue) -->
          <img
            alt=""
            class="absolute inset-0 size-full object-contain"
            :src="levelTier.badge"
            :style="{
              filter: `drop-shadow(0 0 8px ${hexAlpha(levelTier.color, 0.6)})`,
            }"
          />
        </div>
        <div class="flex flex-1 flex-col gap-[10px]">
          <div class="flex items-center justify-between">
            <p class="text-[12px] text-white">
              <span class="font-semibold">Nível </span>
              <span class="font-extrabold">{{ levelTier.name }}</span>
              <span class="font-medium text-white/[0.55]">
                · Lv {{ level }}
              </span>
            </p>
            <button
              class="text-[12.5px] font-extrabold text-v2-blue-light"
              type="button"
              @click="handleVerNiveis"
            >
              Ver níveis ›
            </button>
          </div>
          <div class="h-[6px] w-full rounded-full bg-white/[0.14]">
            <div
              class="h-[6px] rounded-full bg-gradient-to-r from-v2-blue-light to-v2-blue-lighter"
              :style="{ width: `${levelProgressRounded}%` }"
            />
          </div>
          <div
            class="flex items-center justify-between text-[12px] font-semibold text-white"
          >
            <p>{{ levelXpLabel }}</p>
            <p>{{ levelProgressRounded }}%</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== DESKTOP ===================== -->
    <div class="hidden w-full items-center gap-[24px] lg:flex">
      <!-- avatar + nome + VIP: min-w-0 + flex-1 (e NÃO shrink-0) de propósito
           — com shrink-0 um apelido longo empurrava o card de nível e os
           botões Depositar/Sacar pra debaixo do X absoluto do canto. Agora o
           bloco cede espaço e o nome vira "..." (truncate abaixo). -->
      <div class="flex min-w-0 flex-1 items-center gap-[12px]">
        <UserV2AvatarButton
          :avatar-url="currentUserProfileAvatar"
          @click="handleAvatarClick"
        />

        <!-- min-w-[210px]: garante espaço pro chip "Falar com meu gerente"
             (tier 4+) exibir completo, sem tirar o truncate do nome — o
             nome tem seu PRÓPRIO min-w-0 mais interno, então ainda encolhe
             normalmente; só essa coluna como um todo não fica mais estreita
             que isso. -->
        <div class="flex min-w-[210px] flex-col items-start gap-[7px]">
          <!-- duas linhas: nome (+ lápis) em cima, badge de ID + "Membro
               desde" embaixo — saíram da linha do nome pra sobrar largura
               pro apelido antes do truncate -->
          <div class="flex min-w-0 max-w-full flex-col gap-[4px]">
            <div class="flex min-w-0 max-w-full items-center gap-[5px]">
              <p
                class="truncate text-[16px] font-bold leading-[20px] text-white"
                :title="firstName"
              >
                {{ firstName }}
              </p>
              <button
                aria-label="Editar apelido"
                class="flex-none"
                type="button"
                @click="smarticoUserInfosStore.openChangeNickname()"
              >
                <NuxtIcon
                  class="!size-[11px] text-[11px]"
                  name="user/edit"
                />
              </button>
            </div>
            <UserV2IdBadge
              v-if="currentUserID"
              class="max-w-full shrink-0 self-start"
              :copied="isIdCopied"
              :user-id="currentUserID"
              @copy="copyUserId"
            />
            <p
              class="whitespace-nowrap text-[12px] leading-[16px] text-v2-blue-light"
            >
              {{ memberSince }}
            </p>
          </div>
          <!-- mesma arte por tier do header antigo (components/vip/Tag.vue,
               tag-{tier}.webp) no lugar do selo "USUÁRIO VIP" — regra
               inalterada (showVipBadge, tier 0-3). -->
          <VipTag
            v-if="showVipBadge"
            :height="18"
          />
          <!-- tier 4+: sem selo "USUÁRIO VIP" (o card ao lado já mostra o
               tier) — no lugar dele, o chip do gerente dedicado.
               w-fit + max-w-full (não w-[169px] fixo): 169px já era curto
               demais pro texto completo mesmo sozinho, e ainda vazava por
               cima do card de nível quando a coluna do nome (flex-1/
               min-w-0) ficava mais estreita que isso. w-fit deixa o botão
               do tamanho do conteúdo quando cabe, e só encolhe (truncate)
               se a coluna realmente não tiver espaço. -->
          <button
            v-else-if="showVipManagerCard"
            class="flex h-[29px] w-fit max-w-full min-w-0 items-center gap-[6px] rounded-[12px] border border-solid border-white/10 bg-white/10 pl-[10px] pr-[8px]"
            type="button"
            @click="handleTalkToManager"
          >
            <!-- Figma 19771:52160 ("whatsapp - logo"): logo oficial em disco
                 verde, exportada do Figma — é OUTRO desenho do balão do
                 botão mobile (social/whatsapp-filled), por isso dois SVGs -->
            <NuxtIcon
              class="!size-[20px] shrink-0 text-[20px]"
              filled
              name="social/whatsapp-logo"
            />
            <p class="truncate text-[11px] text-white">
              Falar com meu gerente
            </p>
          </button>
        </div>
      </div>

      <!-- card de nível: sempre real, sem gate de VIP (mesma regra do
           dropdown antigo, components/user/header/UserMenuItems.vue —
           nível/elo é eixo separado do tier VIP) -->
      <div
        class="border-solid relative flex h-[88px] w-[294px] shrink-0 items-center gap-[12px] rounded-[12px] border border-v2-primary bg-v2-primary-100 px-[12px] cursor-pointer"
        @click="handleVerNiveis"
      >
        <div class="relative h-[64px] w-[56px] shrink-0 overflow-hidden">
          <!-- brilho na cor do elo atrás do emblema — mesmo drop-shadow da
               lista "Todos os níveis" (components/levels/AllTiers.vue) -->
          <img
            alt=""
            class="absolute inset-0 size-full object-contain"
            :src="levelTier.badge"
            :style="{
              filter: `drop-shadow(0 0 8px ${hexAlpha(levelTier.color, 0.6)})`,
            }"
          />
        </div>
        <div class="flex flex-1 flex-col gap-[8px]">
          <div class="flex items-center justify-between text-[12px]">
            <p class="font-bold text-white">{{ currentLevelLabel }}</p>
            <button
              class="text-v2-blue-light"
              type="button"
            >
              Ver níveis
            </button>
          </div>
          <div class="w-full rounded-full bg-white/20">
            <div
              class="h-[10px] rounded-full bg-gradient-to-r from-v2-blue-light to-v2-blue-lighter"
              :style="{ width: `${levelProgressRounded}%` }"
            />
          </div>
          <div
            class="flex items-center justify-between text-[12px] font-bold text-white"
          >
            <p>{{ levelXpLabel }}</p>
            <p>{{ levelProgressRounded }}%</p>
          </div>
        </div>
      </div>

      <!-- divisória vertical -->
      <div class="h-[86px] w-px shrink-0 bg-white/10" />

      <!-- Depositar / Sacar (Depositar some em só-saque; Sacar cinza quando
           o saque não está liberado — paridade com o header antigo) -->
      <UserV2WalletActionButtons />
    </div>

    <!-- fechar o painel (só aqui no desktop, no canto do header, 16px da
         borda direita e do topo — no mobile o botão fica flutuante por cima
         do painel inteiro, ver components/user-v2/Shell.vue) -->
    <button
      aria-label="Fechar"
      class="absolute right-[16px] top-[16px] hidden size-[40px] items-center justify-center rounded-[9px] bg-white/5 lg:flex"
      @click="close"
    >
      <svg
        fill="none"
        height="40"
        viewBox="0 0 40 40"
        width="40"
      >
        <path
          d="M26.8455 15.4533L28 14.3039L25.6961 12L24.5467 13.1494L20 17.6961L15.4533 13.1494L14.3039 12L12 14.3039L13.1494 15.4533L17.6961 20L13.1545 24.5467L12 25.6961L14.3039 28L15.4533 26.8506L20 22.3039L24.5467 26.8506L25.6961 28L28 25.6961L26.8506 24.5467L22.3039 20L26.8506 15.4533H26.8455Z"
          fill="white"
        />
      </svg>
    </button>

    <!-- notificações (Figma node 19771:52160, "Perfil Desktop", botão +
         node 22854:69744, "Tooltip", popover): mesmo botão do fechar, 7px
         abaixo dele (16 + 40 + 7 = 63) -->
    <div
      ref="notificationsWrapperRef"
      class="absolute right-[16px] top-[63px] hidden lg:block"
    >
      <button
        aria-label="Notificações"
        class="relative flex size-[40px] items-center justify-center rounded-[9px] bg-white/5"
        type="button"
        @click="isNotificationsPopoverOpen = !isNotificationsPopoverOpen"
      >
        <svg
          v-if="isNotificationsEnabled"
          fill="none"
          height="21"
          viewBox="0 0 18 21"
          width="18"
        >
          <path
            d="M10.1824 0H7.63683V2.03649C4.73325 2.62516 2.54561 5.19464 2.54561 8.27324V11.7735L0 14.6373V16.5465H17.8193V14.6373L15.2737 11.7735V8.27324C15.2737 5.19464 13.086 2.62516 10.1824 2.03649V0ZM11.4552 17.8193H8.90964H6.36403C6.36403 18.4955 6.63052 19.1438 7.10782 19.6211C7.58513 20.0984 8.23346 20.3649 8.90964 20.3649C9.58582 20.3649 10.2342 20.0984 10.7115 19.6211C11.1888 19.1438 11.4552 18.4955 11.4552 17.8193Z"
            fill="white"
          />
        </svg>
        <!-- desativado (Figma 23223:78052, "bell-slash") -->
        <svg
          v-else
          fill="none"
          height="16"
          viewBox="0 0 25.4521 20.3649"
          width="20"
        >
          <path
            d="M21.6377 15.8942V14.6373L19.0921 11.7735V8.27324C19.0921 5.19464 16.9045 2.62516 14.0009 2.03649V0H11.4553V2.03649C9.7489 2.38253 8.29312 3.40873 7.3783 4.82473L1.92512 0.588673L1.16939 0.00397752L0 1.51146L0.755728 2.09615L23.527 19.7762L24.2827 20.3609L25.4521 18.8534L24.6964 18.2687L21.6377 15.8942ZM16.1567 16.5465L6.36403 8.83407V11.7735L3.81842 14.6373V16.5465H16.1567ZM15.2737 17.8193H12.7281H10.1824C10.1824 18.4955 10.4489 19.1438 10.9262 19.6211C11.4035 20.0984 12.0519 20.3649 12.7281 20.3649C13.4042 20.3649 14.0526 20.0984 14.5299 19.6211C15.0072 19.1438 15.2737 18.4955 15.2737 17.8193Z"
            fill="white"
          />
        </svg>
      </button>

      <!-- popover: à esquerda do sino, seta apontando pra ele -->
      <div
        v-if="isNotificationsPopoverOpen"
        class="absolute right-[52px] top-1/2 flex -translate-y-1/2 items-center gap-[12px] rounded-[16px] bg-v2-default-300 px-[12px] py-[6px] shadow-[0_10px_16px_rgba(0,0,0,0.25)]"
      >
        <p class="whitespace-nowrap text-[16px] leading-[22px] text-white">
          Notificações
        </p>
        <button
          :aria-checked="isNotificationsEnabled"
          class="flex h-[27px] w-[54px] shrink-0 items-center rounded-full p-[3px] transition-colors"
          :class="[
            isNotificationsEnabled
              ? 'justify-end bg-v2-success'
              : 'justify-start bg-white/10',
            isSavingNotifications ? 'opacity-60' : '',
          ]"
          :disabled="isSavingNotifications"
          role="switch"
          type="button"
          @click="toggleNotifications"
        >
          <span class="size-[22px] rounded-full bg-white shadow" />
        </button>
        <div
          class="absolute left-full top-1/2 -translate-y-1/2 border-8 border-solid border-transparent border-l-v2-default-300"
        />
      </div>
    </div>
  </header>
</template>
